import { z } from "zod";
import { common as enCommon } from "./en/common.ts";
import { common as esCommon } from "./es/common.ts";
import { notifications as enNotifications } from "./en/notifications.ts";
import { notifications as esNotifications } from "./es/notifications.ts";

export const resources = {
  en: { common: enCommon },
  es: { common: esCommon },
} as const;

const notificationResources = {
  en: enNotifications,
  es: esNotifications,
} as const;

export type SupportedLanguage = keyof typeof notificationResources;
export const SUPPORTED_LANGUAGES = ["en", "es"] as const satisfies [
  SupportedLanguage,
  ...SupportedLanguage[],
];

export type NotificationTextKey = keyof typeof enNotifications;

/** Recursive type to extract all dot-notated keys from a nested object. */
type PathsToLeaves<T> = T extends object
  ? {
      [K in keyof T]: `${Exclude<K, symbol>}${PathsToLeaves<T[K]> extends never ? "" : `.${PathsToLeaves<T[K]>}`}`;
    }[keyof T]
  : never;

/** All valid dot-notated translation keys in the system. */
export type TranslationKey = PathsToLeaves<typeof resources.en.common>;

export const TranslationKeySchema = z.string().min(1);

export type TranslationResources = typeof resources;

/** Resolves notification title+body for a key/language, interpolating {{param}} tokens. Fallback: 'en'. */
export function resolveNotificationText(
  key: NotificationTextKey,
  lang: string,
  params: Record<string, string>,
): { title: string; body: string } {
  const safeLang: SupportedLanguage = lang === "es" ? "es" : "en";
  const texts = notificationResources[safeLang][key];

  const interpolate = (template: string): string =>
    template
      .replace(/\{\{(\w+)\}\}/g, (_, k: string) => params[k] ?? "")
      .replace(/\s*—\s*$/, "")
      .trimEnd();

  return {
    title: interpolate(texts.title),
    body: interpolate(texts.body),
  };
}
